/**
 * Generic per-type renderer for App Settings fields.
 *
 * The page passes the current draft value + an ``onChange`` setter; this
 * component dispatches by field type. Atomic types render inline; the
 * composite ``businessHours`` type delegates to ``BusinessHoursEditor``.
 *
 * Reset-to-default behaviour: the page wraps each field with a button
 * that calls ``onChange(field.resetValue)`` — no renderer involvement.
 *
 * Helper text is the page's job too: ``FieldRow`` renders ``field.helperText``
 * above the control, so the inputs here pass only ``error`` to their own
 * ``helperText`` slot. Passing the description as well printed every one twice.
 * The ``' '`` fallback is deliberate — it keeps the helper line's height so
 * showing an error does not shift the layout.
 */

import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteIcon from '@mui/icons-material/Delete';
import type {
  BusinessHoursConfig,
  EmailSendingConfig,
  ProductCodeOption,
} from '@techgitter/contracts';
import {
  BUSINESS_HOURS_DEFAULT,
  PRODUCT_CODE_DEFAULTS,
} from '@techgitter/contracts';

import { CHECKBOX_SX } from '../../styles/designSystem';
import type { SettingField } from './settingsSchema';
import { COMPACT_FIELD_SX, SWITCH_ACCENT_SX } from './fieldStyles';
import { EmailSendingEditor } from './EmailSendingEditor';

export interface SettingsFieldRendererProps {
  field: SettingField;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  /** Error string from validation; renders below the input as helperText. */
  error?: string;
  /** Optional unsaved-marker + reset-to-default control. Composite editors
   *  that own a primary toggle (businessHours) render it inline beside it. */
  resetControl?: React.ReactNode;
}

const DAY_LABELS = [
  { idx: 1, short: 'Mon' },
  { idx: 2, short: 'Tue' },
  { idx: 3, short: 'Wed' },
  { idx: 4, short: 'Thu' },
  { idx: 5, short: 'Fri' },
  { idx: 6, short: 'Sat' },
  { idx: 0, short: 'Sun' },
];

const HOURS_24 = Array.from({ length: 25 }, (_, h) => h);

const formatHour = (h: number): string => {
  if (h === 0) return '12 AM';
  if (h === 12) return '12 PM';
  if (h === 24) return '12 AM (next day)';
  return h < 12 ? `${h} AM` : `${h - 12} PM`;
};

export const SettingsFieldRenderer: React.FC<SettingsFieldRendererProps> = ({
  field,
  value,
  onChange,
  disabled,
  error,
  resetControl,
}) => {
  if (field.type === 'businessHours') {
    return (
      <BusinessHoursEditor
        value={value as BusinessHoursConfig | null}
        onChange={(v) => onChange(v)}
        disabled={disabled}
        error={error}
        resetControl={resetControl}
      />
    );
  }

  if (field.type === 'emailSending') {
    return (
      <EmailSendingEditor
        value={value as EmailSendingConfig | null}
        onChange={(v) => onChange(v)}
        disabled={disabled}
        error={error}
      />
    );
  }

  if (field.type === 'productCodes') {
    return (
      <ProductCodesEditor
        value={value as ProductCodeOption[] | null}
        onChange={(v) => onChange(v)}
        disabled={disabled}
        error={error}
      />
    );
  }

  if (field.type === 'integer') {
    const str =
      value === null || value === undefined ? '' : String(value as number);
    return (
      <TextField
        size="small"
        type="number"
        label={field.label}
        value={str}
        placeholder={field.placeholder}
        onChange={(e) => {
          const t = e.target.value.trim();
          if (t === '') {
            onChange(field.nullable ? null : 0);
            return;
          }
          const n = Number(t);
          onChange(Number.isFinite(n) ? n : t);
        }}
        inputProps={{ min: field.min, max: field.max, step: 1 }}
        InputProps={
          resetControl
            ? {
                endAdornment: (
                  <InputAdornment position="end">{resetControl}</InputAdornment>
                ),
              }
            : undefined
        }
        error={Boolean(error)}
        helperText={error || ' '}
        disabled={disabled}
        sx={{ width: 280 }}
      />
    );
  }

  if (field.type === 'boolean') {
    // Tri-state in storage (true / false / null), binary in the UI: null means
    // "never set", which behaves as off, so an unchecked switch is honest for
    // both. Reset writes null back rather than false, matching every other
    // field's "unset" — see `resetValue` in the schema.
    const checked = value === true;
    return (
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label={field.label}
          sx={{ '& .MuiFormControlLabel-label': { fontSize: 14 } }}
        />
        {error && (
          <Typography sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  if (field.type === 'string' || field.type === 'timezone') {
    const str = (value as string | null | undefined) ?? '';
    // A message body needs to be readable in full while it is edited, so a
    // multiline field also takes the wider column — 320px would wrap the
    // default OTP text into a ribbon.
    const multiline = 'multiline' in field && Boolean(field.multiline);
    return (
      <TextField
        size="small"
        label={field.label}
        value={str}
        placeholder={field.placeholder}
        onChange={(e) => onChange(e.target.value)}
        error={Boolean(error)}
        helperText={error || ' '}
        disabled={disabled}
        multiline={multiline}
        minRows={multiline ? ('rows' in field ? field.rows : undefined) ?? 3 : undefined}
        sx={{ width: multiline ? 520 : 320 }}
      />
    );
  }

  return null;
};

// ---------------------------------------------------------------------------
// BusinessHoursEditor — composite editor for the BusinessHoursConfig object.
// ---------------------------------------------------------------------------

interface BusinessHoursEditorProps {
  value: BusinessHoursConfig | null;
  onChange: (next: BusinessHoursConfig | null) => void;
  disabled?: boolean;
  error?: string;
  resetControl?: React.ReactNode;
}

const BusinessHoursEditor: React.FC<BusinessHoursEditorProps> = ({
  value,
  onChange,
  disabled,
  error,
  resetControl,
}) => {
  // Effective values shown in the inputs while editing — when the user
  // toggles "Override default" on, we seed from BUSINESS_HOURS_DEFAULT.
  const enabled = value !== null;
  const v: BusinessHoursConfig = value ?? BUSINESS_HOURS_DEFAULT;

  const patch = (p: Partial<BusinessHoursConfig>) => {
    onChange({ ...v, ...p });
  };

  return (
    <Stack spacing={2.5}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        flexWrap="wrap"
        sx={{ rowGap: 0.5 }}
      >
        <FormControlLabel
          sx={{ mr: 0 }}
          control={
            <Switch
              checked={enabled}
              onChange={(e) =>
                onChange(e.target.checked ? { ...BUSINESS_HOURS_DEFAULT } : null)
              }
              disabled={disabled}
              sx={SWITCH_ACCENT_SX}
            />
          }
          label={
            enabled
              ? 'Override default business hours'
              : 'Using application default (Sydney 9–5, Mon–Fri)'
          }
        />
        {resetControl}
      </Stack>

      {enabled && (
        <Stack spacing={2.5}>
          <TextField
            size="small"
            label="Timezone"
            value={v.timezone}
            onChange={(e) => patch({ timezone: e.target.value })}
            disabled={disabled}
            sx={[COMPACT_FIELD_SX, { width: 238 }]}
            helperText="IANA zone (e.g. Australia/Sydney)"
          />

          <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <HourSelect
              label="Shoulder start"
              value={v.shoulderStartHour}
              onChange={(h) => patch({ shoulderStartHour: h })}
              disabled={disabled}
            />
            <HourSelect
              label="Business start"
              value={v.businessStartHour}
              onChange={(h) => patch({ businessStartHour: h })}
              disabled={disabled}
            />
            <HourSelect
              label="Business end"
              value={v.businessEndHour}
              onChange={(h) => patch({ businessEndHour: h })}
              disabled={disabled}
            />
            <HourSelect
              label="Shoulder end"
              value={v.shoulderEndHour}
              onChange={(h) => patch({ shoulderEndHour: h })}
              disabled={disabled}
            />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Work days
            </Typography>
            <Stack direction="row" spacing={0}>
              {DAY_LABELS.map(({ idx, short }) => {
                const checked = v.workDays.includes(idx);
                return (
                  <FormControlLabel
                    key={idx}
                    sx={{ ml: 0, mr: 1 }}
                    control={
                      <Checkbox
                        size="small"
                        sx={CHECKBOX_SX}
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? v.workDays.filter((d) => d !== idx)
                            : [...v.workDays, idx].sort((a, b) => a - b);
                          patch({ workDays: next });
                        }}
                        disabled={disabled}
                      />
                    }
                    label={short}
                  />
                );
              })}
            </Stack>
          </Box>

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

interface HourSelectProps {
  label: string;
  value: number;
  onChange: (h: number) => void;
  disabled?: boolean;
}

const HourSelect: React.FC<HourSelectProps> = ({
  label,
  value,
  onChange,
  disabled,
}) => (
  <TextField
    select
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    SelectProps={{ native: true }}
    disabled={disabled}
    sx={[COMPACT_FIELD_SX, { width: 180 }]}
  >
    {HOURS_24.map((h) => (
      <option key={h} value={h}>
        {formatHour(h)}
      </option>
    ))}
  </TextField>
);

// ---------------------------------------------------------------------------
// ProductCodesEditor — composite editor for the productCodes catalog.
// ---------------------------------------------------------------------------

interface ProductCodesEditorProps {
  value: ProductCodeOption[] | null;
  onChange: (next: ProductCodeOption[] | null) => void;
  disabled?: boolean;
  error?: string;
}

const ProductCodesEditor: React.FC<ProductCodesEditorProps> = ({
  value,
  onChange,
  disabled,
  error,
}) => {
  // Null = "use built-in defaults". Toggling the override switch seeds
  // the editable list from PRODUCT_CODE_DEFAULTS so the admin starts
  // from a working baseline.
  const enabled = value !== null;
  const rows: ProductCodeOption[] = value ?? PRODUCT_CODE_DEFAULTS;

  const replace = (next: ProductCodeOption[]) => onChange(next);

  const updateRow = (idx: number, patch: Partial<ProductCodeOption>) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    replace(next);
  };

  const removeRow = (idx: number) => {
    replace(rows.filter((_, i) => i !== idx));
  };

  const moveRow = (idx: number, delta: number) => {
    const target = idx + delta;
    if (target < 0 || target >= rows.length) return;
    const next = rows.slice();
    const [item] = next.splice(idx, 1);
    next.splice(target, 0, item);
    replace(next);
  };

  const addRow = () => {
    // Default group seeds from the last row so new entries usually
    // land next to their siblings without extra clicks.
    const lastGroup = rows[rows.length - 1]?.group ?? '';
    replace([
      ...rows,
      { code: '', label: '', deviceType: undefined, group: lastGroup || undefined },
    ]);
  };

  // Duplicate-code detection — flagged inline so the admin sees the
  // issue before hitting Save (the backend will also reject it).
  const dupCodes = new Set<string>();
  const seenCodes = new Set<string>();
  rows.forEach((r) => {
    const c = r.code.trim();
    if (!c) return;
    if (seenCodes.has(c)) dupCodes.add(c);
    seenCodes.add(c);
  });

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? PRODUCT_CODE_DEFAULTS.map((r) => ({ ...r }))
                  : null,
              )
            }
            disabled={disabled}
          />
        }
        label={
          enabled
            ? 'Custom product code catalog'
            : 'Using built-in defaults (Laptop, Desktop, All-in-One)'
        }
      />

      {enabled && (
        <Stack spacing={1}>
          {rows.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No product codes — add at least one row.
            </Typography>
          )}
          {rows.map((row, idx) => {
            const codeTrim = row.code.trim();
            const isDup = codeTrim !== '' && dupCodes.has(codeTrim);
            return (
              <Stack
                key={idx}
                direction="row"
                spacing={1}
                alignItems="flex-start"
              >
                <TextField
                  size="small"
                  label="Code"
                  value={row.code}
                  onChange={(e) => updateRow(idx, { code: e.target.value })}
                  disabled={disabled}
                  error={isDup}
                  helperText={isDup ? 'Duplicate' : ' '}
                  sx={{ width: 200 }}
                />
                <TextField
                  size="small"
                  label="Label"
                  value={row.label}
                  onChange={(e) => updateRow(idx, { label: e.target.value })}
                  disabled={disabled}
                  helperText=" "
                  sx={{ flex: 1, minWidth: 140 }}
                />
                <TextField
                  size="small"
                  label="Device type (optional)"
                  value={row.deviceType ?? ''}
                  onChange={(e) =>
                    updateRow(idx, {
                      deviceType:
                        e.target.value.trim() === '' ? undefined : e.target.value,
                    })
                  }
                  disabled={disabled}
                  helperText=" "
                  sx={{ width: 180 }}
                />
                <TextField
                  size="small"
                  label="Group (optional)"
                  value={row.group ?? ''}
                  onChange={(e) =>
                    updateRow(idx, {
                      group: e.target.value.trim() === '' ? undefined : e.target.value,
                    })
                  }
                  disabled={disabled}
                  helperText=" "
                  sx={{ width: 180 }}
                />
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveRow(idx, -1)}
                      disabled={disabled || idx === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => moveRow(idx, 1)}
                      disabled={disabled || idx === rows.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Remove">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => removeRow(idx)}
                      disabled={disabled}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          })}

          <Box>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addRow}
              disabled={disabled}
            >
              Add product code
            </Button>
          </Box>

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

